// components/ClanCrewAdvancedNotificationModal.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Modal, Card, Select, Input, Button, message, Spin, Checkbox, Empty } from 'antd';
import { Send, Users, X } from 'lucide-react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { GET_User_Details_User_Management_For_ClanCrew, Get_States, Get_City, Current_Designation, CREATE_COHORT, CREATE_WEB_BULK_CAMPAIGN, GET_LIST_OF_COHORTS } from '../../services/userAPI';

import { useLocation } from 'react-router-dom';

const { Option } = Select;

const perfMap = {
    HIGH: 'High',
    MEDIUM: 'Medium',
    LOW: 'Low',
};

const ClanCrewAdvancedNotificationModal = ({
    visible,
    onCancel,
    accountId,
}) => {
    const [title, setTitle] = useState('');
    const [messageBody, setMessageBody] = useState('');
    const [ctaText, setCtaText] = useState('');
    const [ctaLink, setCtaLink] = useState('');
    const [searchName, setSearchName] = useState('');
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [autoSelectedOnce, setAutoSelectedOnce] = useState(false);

    const [filters, setFilters] = useState({
        state: '',
        stateId: null,
        city: '',
        cityId: null,
        designation: '',
        performanceBand: '',
    });
    const [cohortName, setCohortName] = useState('');
    const [savedCohortId, setSavedCohortId] = useState(null);
    const [selectedCohortId, setSelectedCohortId] = useState(null);
    const location = useLocation();

    // New: For live @mentions
    const editorRef = useRef(null);
    const [mentionedUserIds, setMentionedUserIds] = useState([]);
    const [mentionedNames, setMentionedNames] = useState([]);
    const roleId = useLocation().state?.roleId;

    const [fetchUsers, { data: usersData, loading: usersLoading }] = useLazyQuery(
        GET_User_Details_User_Management_For_ClanCrew,
        { fetchPolicy: 'network-only' }
    );

    const [createCohort, { loading: creatingCohort }] = useMutation(CREATE_COHORT);
    const [createWebBulkCampaign, { loading: creatingCampaign }] = useMutation(CREATE_WEB_BULK_CAMPAIGN);

    const { data: cohortsData, refetch: refetchCohorts } = useQuery(GET_LIST_OF_COHORTS, {
        variables: {
            accountId: accountId ? parseInt(accountId) : null,
            jobroleId: roleId ? parseInt(roleId) : null,
        },
        fetchPolicy: 'network-only',
    });

    // Master data for filters
    const { data: statesData } = useQuery(Get_States);

    const [fetchCities, { data: citiesData }] = useLazyQuery(Get_City, {
        fetchPolicy: 'cache-and-network',
    });

    const { data: designationsData } = useQuery(Current_Designation, {
        variables: { jobroleId: roleId },
        skip: !roleId,
    });

    const [errors, setErrors] = useState({ title: '', messageBody: '' });

    const validateField = (field, value) => {
        if (!value.trim()) {
            setErrors(prev => ({ ...prev, [field]: 'This field is required' }));
        } else {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    // Fetch users when modal opens or filters change
    useEffect(() => {
        if (visible && roleId) {
            refetchUsers();
        }
    }, [visible, roleId, filters, searchName]);

    const refetchUsers = useCallback(() => {
        const gqlFilters = [];

        if (filters.state) gqlFilters.push({ field: "state", value: filters.state });
        if (filters.city) gqlFilters.push({ field: "city", value: filters.city });
        if (filters.designation) gqlFilters.push({ field: "designation", value: filters.designation });
        if (filters.performanceBand) {
            const perfKey = Object.keys(perfMap).find(k => perfMap[k] === filters.performanceBand);
            if (perfKey && perfKey !== 'UNKNOWN') {
                gqlFilters.push({ field: "performanceResidency.performance", value: perfKey });
            }
        }

        // Move name search inside filters array instead of using filterByName variable
        if (searchName) {
            gqlFilters.push({ field: "name", value: searchName });
        }

        fetchUsers({
            variables: {
                roleId: parseInt(roleId),
                filters: gqlFilters.length > 0 ? gqlFilters : null,
            },
        });
    }, [fetchUsers, roleId, filters, searchName]);

    const cohorts = useMemo(
        () => cohortsData?.getListOfCohorts || [],
        [cohortsData]
    );

    const users = useMemo(() => usersData?.getUsersForAccount?.data || [], [usersData]);

    const filteredUsers = useMemo(() => {
        let list = users;
        if (searchName) {
            list = list.filter(u => u.name?.toLowerCase().includes(searchName.toLowerCase()));
        }
        return list;
    }, [users, searchName]);

    useEffect(() => {
        if (!users || users.length === 0) return;
        const currentIds = users.map(u => u.id);
        setSelectedUserIds(prev => prev.filter(id => currentIds.includes(id)));
    }, [users]);

    useEffect(() => {
        if (visible && !autoSelectedOnce && filteredUsers.length > 0) {
            setSelectedUserIds(filteredUsers.map(u => u.id));
            setAutoSelectedOnce(true);
        }
    }, [visible, filteredUsers, autoSelectedOnce]);

    // State / City / Designation master lists from GraphQL
    const states = useMemo(
        () => (statesData?.getStates?.map((state) => state.stateName).filter(Boolean).sort()) || [],
        [statesData]
    );

    useEffect(() => {
        if (!filters.state || !statesData?.getStates) return;
        const selectedState = statesData.getStates.find((s) => s.stateName === filters.state);
        if (selectedState) {
            fetchCities({ variables: { stateId: selectedState.id } });
        }
    }, [filters.state, statesData, fetchCities]);

    const cities = useMemo(() => {
        if (!filters.state || !citiesData?.getCity) return [];
        return citiesData.getCity.map((city) => city.cityName).filter(Boolean).sort();
    }, [citiesData, filters.state]);

    const designations = useMemo(
        () => (designationsData?.getDesignationsJobroleid?.map((d) => d.designation).filter(Boolean).sort()) || [],
        [designationsData]
    );

    const performanceBands = useMemo(() => ['Low', 'Medium', 'High'], []);

    const toggleUser = (userId) => {
        setSelectedUserIds(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const toggleAll = () => {
        if (selectedUserIds.length === filteredUsers.length && filteredUsers.length > 0) {
            setSelectedUserIds([]);
        } else {
            setSelectedUserIds(filteredUsers.map(u => u.id));
        }
    };

    const totalSelected = selectedUserIds.length;

    const handleSaveCohort = async () => {
        if (!cohortName.trim()) return message.error("Please enter a cohort name");
        if (totalSelected === 0) return message.error("No users selected");

        // Build filters as an array of { field, value } objects
        const cohortFilters = [];

        if (filters.state) {
            cohortFilters.push({ field: "stateName", value: filters.state });
        }
        if (filters.city) {
            cohortFilters.push({ field: "cityName", value: filters.city });
        }
        if (filters.designation) {
            cohortFilters.push({ field: "designation", value: filters.designation });
        }
        if (filters.performanceBand) {
            const perfKey = Object.keys(perfMap).find(k => perfMap[k] === filters.performanceBand);
            if (perfKey) {
                cohortFilters.push({ field: "performanceResidency.performance", value: perfKey });
            }
        }

        const allUserIds = users.map(u => u.id);
        const unselectedUserIds = allUserIds.filter(id => !selectedUserIds.includes(id));

        if (unselectedUserIds.length > 0) {
            cohortFilters.push({ field: "unselectedUserIds", value: unselectedUserIds });
        }

        try {
            const cohortResult = await createCohort({
                variables: {
                    filters: cohortFilters,
                    cohortName: cohortName.trim(),
                    cohortDescription: null,
                    accountId: accountId ? parseInt(accountId) : null,
                    jobroleId: roleId ? parseInt(roleId) : null,
                },
            });

            if (cohortResult.data?.createCohort?.success) {
                const newCohort = cohortResult.data?.createCohort?.data;
                const newCohortId = newCohort?.id;
                if (newCohortId) {
                    setSavedCohortId(newCohortId);
                    setSelectedCohortId(newCohortId);
                }
                message.success(cohortResult.data.createCohort.message || `Cohort "${cohortName.trim()}" created successfully`);
                if (refetchCohorts) {
                    refetchCohorts();
                }
            } else {
                message.error(cohortResult.data?.createCohort?.message || 'Failed to create cohort');
            }
        } catch (error) {
            message.error('Failed to create cohort');
        }
    };

    // MAIN MAGIC: Real-time {{Name}} → @Name pill
    const handleEditorInput = () => {
        const editor = editorRef.current;
        if (!editor) return;

        const text = editor.innerText || '';
        setMessageBody(text);

        // Extract mentions for sending (still works perfectly)
        const mentionMatches = text.match(/{{\s*([^}]+?)\s*}}/g) || [];
        const newIds = [];
        const newNames = [];

        mentionMatches.forEach(match => {
            const rawName = match.replace(/[{}]/g, '').trim();
            const user = users.find(u => {
                const full = u.name.toLowerCase();
                const first = full.split(' ')[0];
                return full === rawName.toLowerCase() || first === rawName.toLowerCase();
            });
            if (user) {
                newIds.push(user.id);
                newNames.push(user.name.split(' ')[0]);
            }
        });

        setMentionedUserIds(newIds);
        setMentionedNames(newNames);

        // Only replace {{Name}} → <span class="mention">Name</span>
        let html = editor.innerHTML;

        const newHtml = html.replace(/{{\s*([^}]+?)\s*}}/g, (match, name) => {
            const cleanName = name.trim();
            const user = users.find(u => {
                const full = u.name.toLowerCase();
                const first = full.split(' ')[0];
                return full === cleanName.toLowerCase() || first === cleanName.toLowerCase();
            });
            const displayName = user ? user.name.split(' ')[0] : cleanName;

            // Add zero-width space after mention to prevent style bleed
            return `<mark class="user-mention">${displayName}</mark>\u200B`;
        });

        if (html !== newHtml) {
            // Save cursor position
            const sel = window.getSelection();
            const cursorOffset = sel.rangeCount > 0 ? sel.getRangeAt(0).startOffset : 0;
            const anchorNode = sel.anchorNode;

            editor.innerHTML = newHtml;

            // Restore cursor position after the mention + space
            try {
                const range = document.createRange();
                const newSel = window.getSelection();

                // Try to place cursor at the end
                range.selectNodeContents(editor);
                range.collapse(false);
                newSel.removeAllRanges();
                newSel.addRange(range);
            } catch (e) {
                // Fallback: just focus the editor
                editor.focus();
            }
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Backspace') {
            const selection = window.getSelection();
            const node = selection.anchorNode;
            if (node?.parentNode?.classList?.contains('mention-pill')) {
                node.parentNode.remove();
                e.preventDefault();
            }
        }
    };

    const resetForm = () => {
        setTitle(''); setMessageBody(''); setCtaText(''); setCtaLink('');
        setSelectedUserIds([]); setSearchName(''); setFilters({ state: '', stateId: null, city: '', cityId: null, designation: '', performanceBand: '' });
        setMentionedUserIds([]); setMentionedNames([]);
        setCohortName('');
        setSavedCohortId(null);
        setSelectedCohortId(null);
        setAutoSelectedOnce(false);
        if (editorRef.current) editorRef.current.innerHTML = '';
    };

    const handleSend = async () => {
        if (!title.trim()) {
            return message.error('Please enter a title');
        }
        if (!messageBody.trim()) {
            return message.error('Please enter a message');
        }
        if (selectedUserIds.length === 0 && mentionedUserIds.length === 0) {
            return message.error('No users selected');
        }
        if (!selectedCohortId) {
            return message.error('Please select a cohort from dropdown');
        }

        try {
            const result = await createWebBulkCampaign({
                variables: {
                    cohortId: selectedCohortId,
                    title: title.trim(),
                    description: messageBody.trim(),
                },
            });

            if (result.data?.createWebBulkCampaign?.success) {
                message.success(result.data.createWebBulkCampaign.message || 'Campaign created successfully');
                onCancel();
                resetForm();
            } else {
                message.error(result.data?.createWebBulkCampaign?.message || 'Failed to create campaign');
            }
        } catch (error) {
            message.error('Failed to create campaign');
        }
    };

    return (
        <Modal
            open={visible}
            onCancel={() => { onCancel(); resetForm(); }}
            footer={null}
            width={1200}
            title={<div className="flex items-center gap-2"><Send size={20} /> Send Targeted Notification</div>}
        >
            <style jsx>{`
    .user-mention {
        background: transparent;
        color: #AD0101;
        font-style: italic;
        font-weight: 600;
        padding: 0 2px;
        border-radius: 3px;
    }

    .message-editor {
        min-height: 140px;
        padding: 16px;
        border: 1px solid #d9d9d9;
        border-radius: 8px;
        outline: none;
        font-size: 15px;
        line-height: 1.6;
        background: white;
        word-wrap: break-word;
    }

    .message-editor:empty:before {
        content: 'Type your message here...';
        color: #aaa;
        pointer-events: none;
    }
`}</style>
            <Spin spinning={usersLoading || creatingCohort || creatingCampaign}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Panel - User Selection */}
                    <Card title={
                        <div className="flex items-center justify-between">
                            <span className="flex items-center gap-2"><Users size={20} /> Select Users</span>
                            {totalSelected > 0 && (
                                <button onClick={() => setSelectedUserIds([])} className="text-sm text-[#AD0101]">
                                    Clear All
                                </button>
                            )}
                        </div>
                    }>
                        <div className="bg-red-50 rounded-lg p-3 mb-4 text-center">
                            <div className="text-sm text-[#AD0101]">Selected Users</div>
                            <div className="text-2xl font-bold text-[#AD0101]">{totalSelected}</div>
                        </div>

                        <div className="space-y-3 mb-4">
                            <div className="flex gap-0">
                                <Input placeholder="Cohort name..." value={cohortName} onChange={e => setCohortName(e.target.value)}
                                    style={{ borderRadius: '6px 0 0 6px', borderRight: 'none' }} />
                                <button onClick={handleSaveCohort}
                                    style={{ borderRadius: '0 6px 6px 0', background: cohortName && totalSelected > 0 && !creatingCohort ? '#AD0101' : '#999', color: 'white', padding: '0 16px' }}
                                    disabled={!cohortName || totalSelected === 0 || creatingCohort}>
                                    {creatingCohort ? 'Saving...' : 'Save Cohort'}
                                </button>
                            </div>

                            <Select allowClear showSearch placeholder="Select State" value={filters.state || undefined}
                                onChange={v => setFilters(p => {
                                    if (!v) {
                                        return { ...p, state: '', stateId: null, city: '', cityId: null };
                                    }
                                    const selectedState = statesData?.getStates?.find(s => s.stateName === v);
                                    return {
                                        ...p,
                                        state: v,
                                        stateId: selectedState?.id || null,
                                        city: '',
                                        cityId: null,
                                    };
                                })} className="w-full">
                                {states.map(s => <Option key={s} value={s}>{s}</Option>)}
                            </Select>

                            <Select allowClear showSearch placeholder="Select City" value={filters.city || undefined}
                                onChange={v => setFilters(p => {
                                    if (!v) {
                                        return { ...p, city: '', cityId: null };
                                    }
                                    const selectedCity = citiesData?.getCity?.find(c => c.cityName === v);
                                    return {
                                        ...p,
                                        city: v,
                                        cityId: selectedCity?.id || null,
                                    };
                                })} disabled={!filters.state} className="w-full">
                                {cities.map(c => <Option key={c} value={c}>{c}</Option>)}
                            </Select>

                            <Select allowClear showSearch placeholder="Filter by designation" value={filters.designation || undefined}
                                onChange={v => setFilters(p => ({ ...p, designation: v || '' }))} className="w-full">
                                {designations.map(d => <Option key={d} value={d}>{d}</Option>)}
                            </Select>

                            <Select allowClear showSearch placeholder="Filter by performance band" value={filters.performanceBand || undefined}
                                onChange={v => setFilters(p => ({ ...p, performanceBand: v || '' }))} className="w-full">
                                {performanceBands.map(b => <Option key={b} value={b}>{b}</Option>)}
                            </Select>

                            {/* <button onClick={toggleAll} className="w-full py-2 border border-gray-300 rounded hover:bg-gray-50">
                                {totalSelected === filteredUsers.length && filteredUsers.length > 0 ? 'Deselect All' : 'Select All Visible'}
                            </button> */}

                            <Input placeholder="Search by name..." value={searchName} onChange={e => setSearchName(e.target.value)} allowClear />

                        </div>

                        <div className="max-h-96 overflow-y-auto space-y-2">
                            {filteredUsers.length === 0 ? <Empty /> : filteredUsers.map(user => {
                                const isSelected = selectedUserIds.includes(user.id);
                                return (
                                    <div key={user.id} onClick={() => toggleUser(user.id)}
                                        className={`p-3 rounded-lg border-2 cursor-pointer transition ${isSelected ? 'border-[#AD0101] bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                                        <Checkbox checked={isSelected} onChange={() => { }} />
                                        <span className="ml-3 font-medium">{user.name}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </Card>

                    {/* Right Panel - Compose */}
                    <Card title="Compose Message" className="lg:col-span-2">
                        <div className="space-y-5">
                            <div>
                                <label className="block text-sm font-medium mb-1">Cohort <span className="text-[#AD0101]">*</span></label>
                                <Select
                                    placeholder="Select Cohort"
                                    value={selectedCohortId || undefined}
                                    onChange={(value) => {
                                        setSelectedCohortId(value || null);
                                        const selected = cohorts.find(c => c.id === value);
                                        if (selected) {
                                            setTitle(selected.cohortName || '');
                                            setMessageBody(selected.description || '');
                                            if (editorRef.current) {
                                                editorRef.current.innerText = selected.description || '';
                                            }
                                        }
                                    }}
                                    allowClear
                                    className="w-full"
                                >
                                    {cohorts.map(c => (
                                        <Option key={c.id} value={c.id}>{c.cohortName}</Option>
                                    ))}
                                </Select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Title <span className="text-[#AD0101]">*</span></label>
                                <Input value={title} onChange={e => setTitle(e.target.value)} onBlur={() => validateField('title', title)}
                                    placeholder="Enter title" />
                                {errors.title && <div className="text-sm text-[#AD0101] mt-1">{errors.title}</div>}
                            </div>

                            {/* MESSAGE WITH REAL-TIME @MENTIONS */}
                            <div>
                                <label className="block text-sm font-medium mb-2">Message <span className="text-[#AD0101]">*</span></label>
                                <div
                                    ref={editorRef}
                                    className="message-editor"
                                    contentEditable
                                    suppressContentEditableWarning
                                    onInput={handleEditorInput}
                                    onKeyDown={handleKeyDown}
                                />
                                {errors.messageBody && <div className="text-sm text-[#AD0101] mt-1">{errors.messageBody}</div>}

                                {/* <div className="mt-3 text-xs text-gray-500">
                                    Tip: Type <code className="bg-gray-100 px-1 rounded">{{Chinmay}}</code> → becomes <span className="mention-pill">@Chinmay</span>
                                </div> */}

                                {/* {mentionedUserIds.length > 0 && (
                                    <div className="mt-4 p-3 bg-amber-50 border border-amber-300 rounded-lg text-amber-900 font-medium">
                                        Sending only to: {mentionedNames.join(', ')}
                                    </div>
                                )} */}
                            </div>

                            {/* <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">CTA Text (optional)</label>
                                    <Input value={ctaText} onChange={e => setCtaText(e.target.value)} placeholder="e.g., View Details" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">CTA Link (optional)</label>
                                    <Input value={ctaLink} onChange={e => setCtaLink(e.target.value)} placeholder="https://..." />
                                </div>
                            </div> */}

                            <div className="flex gap-3 pt-4">
                                <button
                                    onClick={handleSend}
                                    disabled={
                                        creatingCampaign ||
                                        !selectedCohortId ||
                                        !title.trim() ||
                                        !messageBody.trim() ||
                                        (mentionedUserIds.length === 0 && selectedUserIds.length === 0)
                                    }
                                    className={`flex-1 h-10 rounded-md text-white font-medium transition ${creatingCampaign ||
                                        !selectedCohortId ||
                                        !title.trim() ||
                                        !messageBody.trim() ||
                                        (mentionedUserIds.length === 0 && selectedUserIds.length === 0)
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-[#AD0101]'
                                        }`}
                                >
                                    {creatingCampaign ? 'Sending...' : `Send to ${mentionedUserIds.length > 0 ? mentionedUserIds.length : totalSelected} users`}
                                </button>
                            </div>
                        </div>
                    </Card>
                </div>
            </Spin>
        </Modal>
    );
};

export default ClanCrewAdvancedNotificationModal;